import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accountCodeService } from "services";
import {
  AccountCodeForm,
  AccountCodeQuery,
  AccountCodeUpdatePayload,
} from "types";
import SnackbarUtils from "utils/SnackbarUtils";

type AccountCodeListKey = ["account-codes"] | ["account-codes", AccountCodeQuery];
type AccountCodeDetailKey = ["account-codes", "detail", number | string];

const accountCodeKeys = {
  all: ["account-codes"] as const,
  list: (params?: AccountCodeQuery): AccountCodeListKey =>
    params ? ["account-codes", params] : ["account-codes"],
  detail: (id: number | string): AccountCodeDetailKey => [
    "account-codes",
    "detail",
    id,
  ],
};

export function useAccountCode() {
  const queryClient = useQueryClient();

  const fetchList = (params?: AccountCodeQuery, enabled = true) => {
    return useQuery({
      queryKey: accountCodeKeys.list(params),
      queryFn: () => accountCodeService.list(params),
      enabled,
      // staleTime: 1000 * 60 * 3,
      select: (data) => ({
        data: data?.data ?? [],
        totalPage:
          data?.totalPage ?? data?.pagination?.totalPage ?? data?.pagination?.total ?? 1,
      }),
    });
  };

  const fetchDetail = (id: number | string, enabled = true) => {
    return useQuery({
      queryKey: accountCodeKeys.detail(id),
      queryFn: () => accountCodeService.show(id),
      enabled: Boolean(id) && enabled,
      // staleTime: 1000 * 60 * 3,
    });
  };

  const createAccountCode = useMutation({
    mutationFn: (payload: AccountCodeForm) => accountCodeService.store(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200 || res?.statusCode === 201) {
        SnackbarUtils.success(res?.message ?? "Tạo mã tài khoản thành công");
      }
      queryClient.invalidateQueries({ queryKey: accountCodeKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Tạo mã tài khoản thất bại");
      console.error("createAccountCode failed", err);
    },
  });

  const updateAccountCode = useMutation({
    mutationFn: (payload: AccountCodeUpdatePayload) =>
      accountCodeService.update(payload),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Cập nhật mã tài khoản thành công");
      }
      queryClient.invalidateQueries({ queryKey: accountCodeKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Cập nhật mã tài khoản thất bại");
      console.error("updateAccountCode failed", err);
    },
  });

  const deleteAccountCode = useMutation({
    mutationFn: (id: number | string) => accountCodeService.destroy(id),
    onSuccess: (res) => {
      if (res?.statusCode === 200) {
        SnackbarUtils.success(res?.message ?? "Xoá mã tài khoản thành công");
      }
      queryClient.invalidateQueries({ queryKey: accountCodeKeys.all });
      return res;
    },
    onError: (err) => {
      SnackbarUtils.error("Xoá mã tài khoản thất bại");
      console.error("deleteAccountCode failed", err);
    },
  });

  return {
    fetchList,
    fetchDetail,
    createAccountCode,
    updateAccountCode,
    deleteAccountCode,
  };
}
